import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Button } from "./Button";
import { ThemeToggle } from "./ThemeToggle";
import { BRAND, WORDMARK_SUB, WORDMARK_SUB_SHORT, NAV_LINKS } from "@/content/site";
import { cn } from "@/lib/cn";
import logoUrl from "@/assets/logo-djinious-workflow.png";

export function Nav() {
  // The bar is ALWAYS translucent — a frosted panel the page passes under, never a
  // see-through gap. It used to be fully transparent until scrollY > 8, which meant
  // hero copy slid under a bar you could read straight through, and the 300ms colour
  // transition widened that window further. Only the separating hairline is
  // scroll-dependent now, so there is no state that can leave content unreadable.
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Below 1024px the disclosure is the only navigation, so it must be dismissable
  // from the keyboard too, not just by clicking the Menu toggle again.
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);

  return (
    <header
      className={cn(
        // 0.88 + a 24px backdrop blur: the page is clearly visible passing underneath,
        // but the wordmark and nav links still win against bright display type.
        "sticky top-0 z-40 bg-ink/[0.88] backdrop-blur-xl transition-colors duration-300",
        scrolled ? "border-b border-line/10" : "border-b border-transparent",
      )}
    >
      <nav className="mx-auto flex max-w-sheet items-center gap-6 px-6 py-4">
        {/* The lockup is one unit: neither the descriptor nor the logo may wrap or
            shrink, or the header grows a second line on a narrow phone. */}
        <Link to="/" className="flex shrink-0 items-center gap-3" aria-label={`${BRAND} ${WORDMARK_SUB}`}>
          <img src={logoUrl} alt="" loading="lazy" decoding="async" className="h-8 w-8 shrink-0" width={32} height={32} />
          <span className="flex flex-col leading-none">
            <span className="font-wordmark text-lg tracking-tight text-bone">{BRAND}</span>
            {/* Below 360px the descriptor plus the CTA push the body into a horizontal
                scroll, so it drops out there — the brand name still identifies the site.
                The full descriptor is ~2.5x wider again (289px vs 105px), and at 1024
                the six nav links appear alongside it — measured, that overflows by 31px.
                So it waits for 1200 and narrower viewports get the short form rather
                than nothing. Both are one line: the lockup must never wrap. */}
            <span className="mt-1 hidden whitespace-nowrap font-mono text-[0.625rem] uppercase tracking-[0.18em] text-bone-faint min-[360px]:block min-[1200px]:hidden">
              {WORDMARK_SUB_SHORT}
            </span>
            <span className="mt-1 hidden whitespace-nowrap font-mono text-[0.625rem] uppercase tracking-[0.18em] text-bone-faint min-[1200px]:block">
              {WORDMARK_SUB}
            </span>
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-1">
          <ThemeToggle />
          <div className="hidden items-center gap-0.5 lg:flex">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  cn(
                    "rounded px-3 py-2 text-sm font-medium text-bone-dim transition-colors hover:bg-panel/70 hover:text-bone",
                    isActive && "text-bone",
                  )
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="nav-menu"
            className="rounded px-3 py-2 font-mono text-rail uppercase text-bone-dim transition-colors hover:bg-panel/70 hover:text-bone lg:hidden"
          >
            Menu
          </button>
          {/* Below 480px, the theme toggle, the Menu disclosure and this button no
              longer fit the header alongside the wordmark without pushing the page
              into a horizontal scroll. The button reappears at 480px; below it, the
              same action is the last row of the disclosure panel instead. */}
          <Button
            to="/book-demo"
            className="ml-2 hidden whitespace-nowrap px-4 py-2.5 text-xs min-[480px]:inline-flex"
          >
            Book a demo
          </Button>
        </div>
      </nav>

      {/* Six links no longer fit a horizontal strip on a phone, so narrow viewports get
          a real disclosure. The panel is removed from the DOM when closed rather than
          hidden, so its links are not tab-reachable behind a closed menu. */}
      {menuOpen && (
        <div id="nav-menu" className="flex flex-col border-t border-line/10 px-4 py-2 lg:hidden">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  "rounded px-3 py-2.5 text-sm text-bone-dim transition-colors hover:bg-panel/70 hover:text-bone",
                  isActive && "bg-panel/70 text-bone",
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          {/* The header's own Book a demo button is hidden below 480px (see above),
              so the disclosure carries the same action at that width instead of
              dropping it. */}
          <Button
            to="/book-demo"
            onClick={() => setMenuOpen(false)}
            className="mt-2 min-[480px]:hidden"
          >
            Book a demo
          </Button>
        </div>
      )}
    </header>
  );
}
